import { Check, Loader2 } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import type { ProfilePicture } from '@/types';

interface ProfilePicturePickerProps {
  /** Available pictures, one per connected provider. */
  pictures: ProfilePicture[];
  /** Provider slug of the currently active picture. */
  activeSource?: string;
  /** Initials shown when an image fails to load. */
  initials: string;
  /** Source currently being activated (shows a spinner; disables the row). */
  switchingSource?: string | null;
  /** Activate the picture for the given provider slug. */
  onSelect: (source: string) => void;
}

/**
 * Lets the user flip through each connected provider's avatar and pick which one
 * to use. Picture management is provider-driven — there's no custom upload, so we
 * only render already-connected sources.
 */
export function ProfilePicturePicker({
  pictures,
  activeSource,
  initials,
  switchingSource,
  onSelect,
}: ProfilePicturePickerProps) {
  return (
    <div>
      <p className="mb-3 text-xs font-medium text-muted-foreground">
        Profile picture — choose which connected account to use
      </p>
      <div className="flex flex-wrap gap-4">
        {pictures.map((pic) => {
          const isActive = pic.source === activeSource;
          const isSwitching = switchingSource === pic.source;
          return (
            <button
              key={pic.source}
              type="button"
              onClick={() => onSelect(pic.source)}
              disabled={!!switchingSource}
              aria-pressed={isActive}
              title={`Use ${pic.source} picture`}
              className="focus-ring group flex flex-col items-center gap-1.5 rounded-md disabled:cursor-not-allowed"
            >
              <span className="relative">
                <Avatar
                  className={`h-12 w-12 ring-2 ring-offset-2 ring-offset-card transition-colors ${
                    isActive ? 'ring-primary-600' : 'ring-transparent group-hover:ring-border'
                  }`}
                >
                  <AvatarImage src={pic.url} alt={`${pic.source} picture`} />
                  <AvatarFallback className="bg-primary-600 text-white text-sm">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                {isActive && !isSwitching && (
                  <span className="absolute -bottom-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary-600 text-white ring-2 ring-card">
                    <Check className="h-2.5 w-2.5" />
                  </span>
                )}
                {isSwitching && (
                  <span className="absolute inset-0 flex items-center justify-center rounded-full bg-black/40">
                    <Loader2 className="h-4 w-4 animate-spin text-white" />
                  </span>
                )}
              </span>
              <span
                className={`text-xs capitalize ${
                  isActive ? 'font-medium text-foreground' : 'text-muted-foreground'
                }`}
              >
                {pic.source}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
